import { Maybe, Money } from "@oysterhr/schema"
import { currenciesByCurrencyCode } from "../localizationHelpers"

interface PartialMoney extends Pick<Money, "currencyCode" | "subunit"> {
  subunitToUnit?: null | number
}

interface FormatOptions {
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

const DEFAULT_FORMAT_OPTIONS: FormatOptions = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
}

export class SimpleMoney {
  constructor(
    public amount: number,
    public currencyCode: string,
  ) {}

  format(options?: FormatOptions) {
    const formatOptions = {
      ...DEFAULT_FORMAT_OPTIONS,
      ...options,
    }

    return `${this.currencyCode} ${this.amount.toLocaleString(undefined, formatOptions)}`
  }

  /**
   *
   * @example
   * SimpleMoney.from(5000, "USD").format() === "USD 50"
   * // if you already have the unit price amount, then set the subunitToUnit to 1
   * SimpleMoney.from(50, "USD", 1).format() === "USD 50.00"
   */
  static from(amount: number, currencyCode: string, subunitToUnit?: number): SimpleMoney

  /**
   * @example
   * SimpleMoney.from({ amount: 5000, currency: "USD" }).format() === "USD 50.00"
   * // if you already have the unit price amount, then set the subunitToUnit to 1
   * SimpleMoney.from({ amount: 50, currency: "USD", subunitToUnit: 1 }).format() === "USD 50.00"
   */
  static from(money: PartialMoney): SimpleMoney

  // from method implementation
  static from(
    money: number | PartialMoney,
    currencyCode?: string,
    subunitToUnit?: number,
  ): SimpleMoney {
    if (typeof money === "object") {
      return this._fromMoneyObject(money)
    }

    return this._fromMoneyObject({
      currencyCode: currencyCode as string,
      subunit: Number(money),
      subunitToUnit,
    })
  }

  /**
   * @example
   * SimpleMoney.fromNullable(null, "USD")?.format() === null
   * SimpleMoney.fromNullable(5000, "USD")?.format() === "USD 50.00"
   * // if you already have the unit price amount, then set the subunitToUnit to 1
   * SimpleMoney.fromNullable(50, "USD", 1)?.format() === "USD 50.00"
   */
  static fromNullable(
    amount?: Maybe<number>,
    currencyCode?: Maybe<string>,
    subunitToUnit?: Maybe<number>,
  ): Maybe<SimpleMoney>
  /**
   * @example
   * SimpleMoney.fromNullable({ amount: null, currency: "USD" })?.format() === null
   * SimpleMoney.fromNullable({ amount: 5000, currency: "USD" })?.format() === "USD 50.00"
   * // if you already have the unit price amount, then set the subunitToUnit to 1
   * SimpleMoney.fromNullable({ amount: 50, currency: "USD", subunitToUnit: 1 })?.format() === "USD 50.00"
   */
  static fromNullable(money?: Maybe<PartialMoney>): SimpleMoney

  // fromNullable implementation
  static fromNullable(
    money?: Maybe<PartialMoney | number>,
    currencyCode?: Maybe<string>,
    subunitToUnit?: Maybe<number>,
  ) {
    if (money == null) return null
    if (typeof money === "object") {
      return this._fromNullableMoneyObject(money)
    }

    if (currencyCode == null) {
      return null
    }

    return this.from(money, currencyCode, subunitToUnit ?? undefined)
  }

  ceil() {
    return new SimpleMoney(Math.ceil(this.amount), this.currencyCode)
  }

  private static getCurrencySubunitConversionRate(currencyCode: string) {
    return currenciesByCurrencyCode[currencyCode]?.subunit_to_unit
  }

  private static _fromMoneyObject(money: PartialMoney): SimpleMoney {
    const subunitToUnit =
      money.subunitToUnit ?? this.getCurrencySubunitConversionRate(money.currencyCode) ?? 100
    const amount = money.subunit / subunitToUnit
    const currencyCode = money.currencyCode
    return new SimpleMoney(amount, currencyCode)
  }

  private static _fromNullableMoneyObject(money?: Maybe<PartialMoney>) {
    if (money?.subunit == null || money?.currencyCode == null) {
      return null
    }
    return this._fromMoneyObject(money)
  }
}
